import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Collapse, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import clsx from 'clsx';
import { useRouteScenario } from 'src/hooks/useRouteScenario';
import { useLocationHelpers } from 'components/location/hooks/useLocationHelpers';
import { OrderToTableLocationPicker } from 'components/location/OrderToTableLocationPicker';
import { IOrderReadResourceV18, OrderScenario } from 'components/order/model/Order';
import { TimeslotPicker } from 'components/timeslots/picker/TimeslotPicker';
import { addOpacity, decompoundKey, getCompoundKey } from 'lib/helpers';
import { ApplicationState } from 'store/store';
import { ICategoryGroup, IEnrichedCategory, IEnrichedMenuWithModifierMaps } from '../model/Menu';
import { RefsMap } from './MenuDesktop';
import { DineInPrepTimeCard } from 'components/timeslots/picker/DineInTimeslotCard';
import { useLocalHistory } from 'lib/useLocalHistory';

const PREFIX = 'MenuSidebar';

const classes = {
    selectedCategoryGroup: `${PREFIX}selectedCategoryGroup`,
    categoryItem: `${PREFIX}categoryItem`,
    categoryItemText: `${PREFIX}categoryItemText`,
    categoryGroupText: `${PREFIX}categoryGroupText`,
    selectedItem: `${PREFIX}selectedItem`,
    nested: `${PREFIX}nested`,
    menuTitle: `${PREFIX}menuTitle`,
    menuCategoryList: `${PREFIX}menuCategoryList`
};

export const StyledMenuTitle = styled(Typography)(({ theme }) => ({
    fontFamily: `TenantFont, ${theme.typography.fontFamily}`,
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'center',
    color: theme.palette.titleTextColour
}));

const StyledBox = styled(Box, {
    shouldForwardProp: prop => prop !== 'showCategoryGroups'
})<{ showCategoryGroups: boolean }>(({ theme, showCategoryGroups }) => ({
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(2),
    height: '100%',
    [`& .${classes.selectedCategoryGroup}`]: {
        backgroundColor: addOpacity(theme.palette.common.black, 0.08)
    },
    [`& .${classes.categoryItem}`]: {
        padding: theme.spacing(2)
    },

    [`& .${classes.categoryItemText}`]: {
        fontWeight: 'inherit',
        color: theme.palette.titleTextColour
    },

    [`& .${classes.categoryGroupText}`]: {
        fontSize: theme.spacing(2.25),
        fontWeight: 'inherit'
    },

    [`& .${classes.selectedItem}`]: {
        fontWeight: theme.typography.fontWeightBold,
        backgroundColor: `${
            showCategoryGroups ? 'transparent' : addOpacity(theme.palette.common.black, 0.08)
        } !important`
    },

    [`& .${classes.nested}`]: {
        paddingLeft: theme.spacing(4)
    },

    [`& .${classes.menuTitle}`]: {
        borderTopLeftRadius: theme.shape.borderRadius,
        borderTopRightRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.background.paper
    },

    [`& .${classes.menuCategoryList}`]: {
        borderBottomLeftRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.background.paper
    }
}));

interface IProps {
    menu: IEnrichedMenuWithModifierMaps | undefined;
    orderAhead: boolean;
    categoryRefs: RefsMap;
    activeCategory: string | undefined;
    categories: IEnrichedCategory[];
    showCategoryGroups: boolean;
    categoryGroups?: ICategoryGroup[];
    recentOrders: IOrderReadResourceV18[] | undefined;
    categoryGroupToCategories: [ICategoryGroup, IEnrichedCategory[]][] | undefined;
}

export const MenuSidebar: React.FC<React.PropsWithChildren<IProps>> = ({
    menu,
    categories,
    orderAhead,
    categoryRefs,
    activeCategory,
    showCategoryGroups,
    categoryGroupToCategories,
    recentOrders
}) => {
    const { t } = useTranslation();

    const { replace } = useLocalHistory();
    const { checkServiceAvailability } = useLocationHelpers();
    const { deliveryLocation } = useSelector((state: ApplicationState) => state.basket);
    const [activeCategoryGroup, setActiveCategoryGroup] = React.useState<string | undefined>();
    React.useEffect(() => {
        if (
            (!recentOrders || (recentOrders && recentOrders.length === 0)) &&
            categoryGroupToCategories &&
            showCategoryGroups
        ) {
            setActiveCategoryGroup(categoryGroupToCategories[0][0].id);
        } else {
            setActiveCategoryGroup(undefined);
        }
    }, [categoryGroupToCategories, recentOrders, showCategoryGroups]);

    React.useEffect(() => {
        const { parentId: categoryGroupId } = decompoundKey(activeCategory || '');
        if (categoryGroupId && showCategoryGroups) {
            setTimeout(() => {
                setActiveCategoryGroup(currentCategoryGroup => {
                    if (currentCategoryGroup !== categoryGroupId) {
                        return categoryGroupId;
                    }

                    return currentCategoryGroup;
                });
            }, 500);
        }
    }, [activeCategory, showCategoryGroups]);
    const [scenario] = useRouteScenario();
    const handleCategoryClick = React.useCallback(
        (refName: string) => () => {
            if (
                refName &&
                categoryRefs[refName] &&
                categoryRefs[refName].ref !== null &&
                categoryRefs[refName].ref.current !== null
            ) {
                (categoryRefs[refName].ref as React.MutableRefObject<HTMLDivElement>).current.scrollIntoView({
                    behavior: 'smooth',
                    inline: 'start'
                });
                categoryRefs[refName].active = true;
            }
        },
        [categoryRefs]
    );

    const renderCategoryItem = React.useCallback(
        (category: IEnrichedCategory, categoryGroupId?: string) => {
            const { childId: categoryId, parentId: activeCategoryParentGroupId } = decompoundKey(
                activeCategory || ''
            );
            const selected =
                categoryId === category.id &&
                (showCategoryGroups ? categoryGroupId === activeCategoryParentGroupId : true);
            return (
                <ListItemButton
                    key={`${categoryGroupId}/${category.id}`}
                    classes={{ selected: classes.selectedItem }}
                    className={clsx(classes.categoryItem)}
                    onClick={handleCategoryClick(getCompoundKey(category.groupCategoryId, category.id))}
                    selected={selected}
                >
                    <Typography variant="body1" className={classes.categoryItemText}>
                        {category.title}
                    </Typography>
                </ListItemButton>
            );
        },
        [activeCategory, handleCategoryClick, showCategoryGroups]
    );
    const renderCategoryItemMapped = React.useCallback(
        (parentCategoryId?: string) => (category: IEnrichedCategory) =>
            renderCategoryItem(category, parentCategoryId),
        [renderCategoryItem]
    );
    const handleCategoryGroupClick = React.useCallback(
        (id: string) => () => {
            setActiveCategoryGroup(active => (active === id ? undefined : id));
        },
        []
    );
    const renderCategoryGroup = React.useCallback(
        ([parentCategory, groupCategories]: [ICategoryGroup, IEnrichedCategory[]]) => {
            const { parentId: parentCategoryId } = decompoundKey(activeCategory || '');
            const selected = parentCategory.id === parentCategoryId;

            return (
                <React.Fragment key={parentCategory.id}>
                    <ListItemButton selected={selected} onClick={handleCategoryGroupClick(parentCategory.id)}>
                        <ListItemText
                            slotProps={{
                                primary: {
                                    color: 'titleTextColour',
                                    fontWeight: 'medium',
                                    variant: 'body1'
                                }
                            }}
                            sx={{
                                width: '100%'
                            }}
                            primary={parentCategory.title}
                        />
                        <Box sx={{ marginLeft: 'auto', color: 'titleTextColour' }}>
                            {activeCategoryGroup === parentCategory.id ? <ExpandLess /> : <ExpandMore />}
                        </Box>
                    </ListItemButton>
                    <Collapse
                        className={clsx({
                            [classes.selectedCategoryGroup]: selected
                        })}
                        in={activeCategoryGroup === parentCategory.id}
                        timeout="auto"
                        unmountOnExit
                    >
                        <List component="div" disablePadding>
                            {groupCategories.map(renderCategoryItemMapped(parentCategory.id))}
                        </List>
                    </Collapse>
                </React.Fragment>
            );
        },
        [activeCategory, activeCategoryGroup, handleCategoryGroupClick, renderCategoryItemMapped]
    );
    const renderRecentOrderItem = React.useMemo(
        () => (
            <ListItemButton
                onClick={handleCategoryClick('recent-orders')}
                selected={activeCategory === 'recent-orders'}
                {...(!showCategoryGroups
                    ? {
                          classes: { selected: classes.selectedItem },
                          className: clsx(classes.categoryItem, {
                              [classes.nested]: showCategoryGroups
                          })
                      }
                    : {})}
            >
                <Typography
                    className={showCategoryGroups ? classes.categoryGroupText : classes.categoryItemText}
                >
                    {t('PREORDER_PREVIOUS_ORDERS')}
                </Typography>
            </ListItemButton>
        ),
        [activeCategory, handleCategoryClick, showCategoryGroups, t]
    );

    const showOrderToTableLocationPicker = React.useMemo(
        () =>
            !!menu &&
            (scenario === OrderScenario.ORDER_TO_TABLE || scenario === OrderScenario.TABLE) &&
            checkServiceAvailability(scenario),
        [checkServiceAvailability, menu, scenario]
    );
    const handleTableChange = React.useCallback(
        ({ current }: { previous?: string; current: string }) => {
            replace('', {}, `tableNumber=${current}`);
        },
        [replace]
    );
    return (
        <StyledBox
            showCategoryGroups={showCategoryGroups}
            display="flex"
            flexDirection="column"
            paddingTop={1}
            height="100%"
        >
            {!!menu && orderAhead && scenario === OrderScenario.PREORDER && <TimeslotPicker />}
            {!!menu && orderAhead && scenario === OrderScenario.DINE_IN && <DineInPrepTimeCard />}

            {showOrderToTableLocationPicker && (
                <OrderToTableLocationPicker
                    isDialogOpenInitially={!deliveryLocation}
                    onTableChange={handleTableChange}
                />
            )}
            <Box display="flex" flexDirection="column" marginTop={2} flex="1" maxHeight="100%">
                <Box paddingTop={3} paddingBottom={2} className={classes.menuTitle}>
                    <StyledMenuTitle variant="h6">{t('OUR_MENU')}</StyledMenuTitle>
                </Box>
                <Box flex={1} overflow="auto" className="hidden-scroll" paddingBottom={10}>
                    <Box className={classes.menuCategoryList}>
                        <List>
                            {!!recentOrders?.length && renderRecentOrderItem}
                            {!showCategoryGroups && !!menu && categories.map(renderCategoryItemMapped())}
                            {showCategoryGroups &&
                                !!menu &&
                                categoryGroupToCategories?.map(renderCategoryGroup)}
                        </List>
                    </Box>
                </Box>
            </Box>
        </StyledBox>
    );
};
